import { ApolloClient, gql, NormalizedCacheObject, useQuery } from '@apollo/client';
import { useIsFeatureEnabled } from '@fluentsms/feature-flags';
import React, { createContext, ReactNode, useContext } from 'react';
import { contentfulClient } from '../client';

const GET_CONFIGURATION = gql`
  query {
    configuration {
      contentful {
        contentDeliveryApiKey
        contentPreviewApiKey
        isPreview
        spaceId
      }
    }
  }
`;

type ContentfulClientProviderProps = {
  children: ReactNode;
};

interface ContentfulClientContextType {
  client?: ApolloClient<NormalizedCacheObject>;
  shouldIncludeDrafts?: boolean;
}

export const ContentfulClientContext = createContext<ContentfulClientContextType>({ shouldIncludeDrafts: false });

export const useContentfulClientContext = () => {
  const context = useContext(ContentfulClientContext);
  if (context === undefined) {
    throw new Error('useContentfulClientContext must be used within a ContentfulClientProvider');
  }
  return context;
};

export const ContentfulClientProvider = ({ children }: ContentfulClientProviderProps) => {
  const shouldIncludeDrafts = useIsFeatureEnabled('app-2346277-contentful-draft-data') || false;

  const { data, loading } = useQuery<{
    configuration: {
      contentful: { contentDeliveryApiKey: string; contentPreviewApiKey: string; isPreview: boolean; spaceId: string };
    };
  }>(GET_CONFIGURATION);

  const CONTENT_API_KEY = shouldIncludeDrafts
    ? data?.configuration?.contentful?.contentPreviewApiKey
    : data?.configuration?.contentful?.contentDeliveryApiKey;

  const contentfulApolloClient = contentfulClient(
    data?.configuration?.contentful?.spaceId ?? '',
    CONTENT_API_KEY ?? '',
  );

  return (
    <ContentfulClientContext.Provider value={{ client: contentfulApolloClient, shouldIncludeDrafts }}>
      {!!data && !loading ? children : ''}
    </ContentfulClientContext.Provider>
  );
};
